import { ComputedInputPropTypes, FormInput } from '@ui-kit'
import { useEffect, useRef } from 'react'
import { FieldValues, useWatch } from 'react-hook-form'
import { evaluateFormula } from '../../utils/formula.utils'

const ComputeInput = <T extends FieldValues>({
  name,
  label,
  type = 'text',
  placeholder = '',
  defaultValue,
  register,
  errors,
  isIcon,
  icon,
  iconClick,
  validation,
  className = '',
  color,
  info,
  formatter,
  staticText,
  dataQaId = '',
  hasPermission = true,
  formula,
  control,
  setValue,
  ...rest
}: ComputedInputPropTypes<T>) => {
  const allValues = useWatch({ control })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const prevFieldsRef = useRef<Record<string, any>>({})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getExtractedFieldValues = (allValues: any, keys: string[]) =>
    keys.reduce((acc, key) => {
      acc[key] = allValues?.[key]
      return acc
    }, {} as Record<string, unknown>)

  const haveFieldsChanged = (
    prev: Record<string, unknown>,
    current: Record<string, unknown>
  ) => {
    return Object.keys(current).some((key) => prev[key] !== current[key])
  }

  const extractFieldsFromFormula = (formula: string | undefined): string[] => {
    if (!formula) return []

    if (formula.includes('calculateDates')) {
      const match =
        RegExp(
          /calculateDates\(\s*\{([^}]*)\}\s*,\s*\{([^}]*)\}\s*,\s*\{([^}]*)\}\s*\)/
        ).exec(formula) || []
      return match
        .slice(1)
        .filter(Boolean)
        .flatMap((s) => s.split(',').map((f) => f.trim()))
    }

    if (formula.includes('concat')) {
      return Array.from(formula.matchAll(/\{([^}]+)\}/g), (m) => m[1])
    }

    return Array.from(formula.matchAll(/\{([^}]+)\}/g), (m) => m[1])
  }

  useEffect(() => {
    const extractedFields: string[] = extractFieldsFromFormula(formula)
    const currentFields = getExtractedFieldValues(allValues, extractedFields)

    if (haveFieldsChanged(prevFieldsRef.current, currentFields)) {
      const computed = evaluateFormula(formula ?? '', allValues)
      setValue?.(name, computed)
      prevFieldsRef.current = currentFields
    }
  }, [allValues, formula, name, setValue])

  return (
    <FormInput
      name={name}
      label={label}
      disabled
      placeholder={placeholder}
      defaultValue={defaultValue}
      register={register}
      errors={errors}
      isIcon={isIcon}
      icon={icon}
      iconClick={iconClick}
      validation={validation}
      color={color}
      info={info}
      formatter={formatter}
      staticText={staticText}
      dataQaId={dataQaId}
      hasPermission={hasPermission}
      tabIndex={rest?.tabIndex}
      type={type}
      className={className}
    />
  )
}

export default ComputeInput
